import PropTypes from 'prop-types';
import { flexRender } from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';

import { cn } from '#/lib/utils';
import useDndSensors from '#/hooks/useDnd';
import { useTableContext } from '#/hooks/useTableContext';
import {
  getOrderableColumnIds,
  isColumnOrderLocked,
} from '#/hooks/table/mergeColumnOrder';
import { renderEntityStatusBadge } from '#/modules/commonComponents/table/entityStatusIconHelpers';
import SortableTableHeaderCell from '#/modules/commonComponents/table/SortableTableHeaderCell';
import {
  DEFAULT_TABLE_TEXT_SIZE,
  tableTextSizeClassName,
} from '#/modules/commonComponents/table/tableTextSize';

function getColumnWidth(column) {
  return column.getSize();
}

function getColumnMinWidth(column) {
  return column.columnDef.minSize ?? column.getSize();
}

const Table = ({
  containerClassName = '',
  tableClassName = '',
  textSize = DEFAULT_TABLE_TEXT_SIZE,
  loading = false,
  rowFn = null,
  onRowDoubleClick = null,
}) => {
  const context = useTableContext();
  const { table } = context;
  const rowSelection = context.rowSelection;
  const setRowSelection = context.setRowSelection;
  const enableColumnOrder = Boolean(context.enableColumnOrder);
  const sensors = useDndSensors();

  const leafColumns = table.getAllLeafColumns();
  const orderableIds = enableColumnOrder
    ? getOrderableColumnIds(leafColumns)
    : [];

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const currentOrder = table.getState().columnOrder?.length
      ? table.getState().columnOrder
      : leafColumns.map((column) => column.id);

    const oldIndex = currentOrder.indexOf(active.id);
    const newIndex = currentOrder.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const overColumn = leafColumns.find((column) => column.id === over.id);
    if (overColumn && isColumnOrderLocked(overColumn)) {
      return;
    }

    table.setColumnOrder(arrayMove(currentOrder, oldIndex, newIndex));
  }

  return (
    <div className={cn(containerClassName || 'table-scroll')}>
      {loading ? (
        <div className="cover_overlay flex items-center justify-center bg-gray-400/65">
          <span className="text-sm text-gray-700">載入中...</span>
        </div>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <table
          className={cn('table', tableTextSizeClassName(textSize), tableClassName)}
          style={{ minWidth: table.getCenterTotalSize() }}
        >
          <thead className="bg-panel shadow-sm shadow-gray-300/65 sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="text-left">
                <SortableContext
                  items={enableColumnOrder
                    ? headerGroup.headers.map((header) => header.column.id)
                    : []}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeaderCell
                      key={header.id}
                      header={header}
                      enableColumnOrder={enableColumnOrder
                        && orderableIds.includes(header.column.id)}
                    />
                  ))}
                </SortableContext>
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isSelected = rowSelection && setRowSelection ? row.getIsSelected() : false;
              const isClickable = rowFn || onRowDoubleClick || (rowSelection && setRowSelection);

              function handleRowClick() {
                if (rowSelection && setRowSelection) {
                  const currentSelected = row.getIsSelected();
                  row.toggleSelected();

                  if (rowFn) {
                    rowFn(row, !currentSelected);
                  }
                } else if (rowFn) {
                  rowFn(row);
                }
              }

              function handleRowDoubleClick() {
                if (onRowDoubleClick) {
                  onRowDoubleClick(row);
                }
              }

              return (
                <tr
                  key={row.id}
                  onClick={isClickable ? handleRowClick : undefined}
                  onDoubleClick={onRowDoubleClick ? handleRowDoubleClick : undefined}
                  className={cn(
                    isSelected && 'table_row_selected',
                    isClickable && 'cursor-pointer',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    if (cell.column.id === 'status') {
                      const isDocumentStatus = cell.column.columnDef.meta?.statusKind === 'document';

                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: getColumnWidth(cell.column),
                            minWidth: getColumnMinWidth(cell.column),
                          }}
                        >
                          {isDocumentStatus
                            ? flexRender(cell.column.columnDef.cell, cell.getContext())
                            : renderEntityStatusBadge(cell.getValue())}
                        </td>
                      );
                    }

                    return (
                      <td
                        key={cell.id}
                        className={cn('truncate', cell.column.columnDef.meta?.cellClassName)}
                        style={{
                          width: getColumnWidth(cell.column),
                          minWidth: getColumnMinWidth(cell.column),
                        }}
                      >
                        {cell.column.columnDef.cell
                          ? flexRender(cell.column.columnDef.cell, cell.getContext())
                          : cell.getValue()}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </DndContext>
    </div>
  );
};

Table.propTypes = {
  containerClassName: PropTypes.string,
  tableClassName: PropTypes.string,
  textSize: PropTypes.oneOf(['sm', 'base']),
  loading: PropTypes.bool,
  rowFn: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
};

export default Table;
